import React from 'react';

import { Avatar } from '@mantine/core';
import {
  IconBrandOpenai,
  IconDeviceMobileMessage,
  IconLock,
  IconLockOpen,
  IconPassword,
  IconPhone
} from '@tabler/icons-react';
import { AxiosProgressEvent } from 'axios';
import { SelectItemProps } from 'components/SelectItem';
import { TokenFactorBadge } from 'components/TokenFactorBadge';
import { UpgradePlanButton } from 'components/UpgradePlanButton';
import { BrowserIcon } from 'hugeicons-react';
import i18n from 'i18n';
import { AnthropicIcon } from 'icons/AnthropicIcon';
import { BigCommerceIcon } from 'icons/BigCommerceIcon';
import { DiscordIcon } from 'icons/DiscordIcon';
import { FacebookIcon } from 'icons/FacebookIcon';
import { GoogleIcon } from 'icons/GoogleIcon';
import { InstagramIcon } from 'icons/InstagramIcon';
import { LogoIcon } from 'icons/LogoIcon';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { SquarespaceIcon } from 'icons/SquarespaceIcon';
import { WhatsAppIcon } from 'icons/WhatsAppIcon';
import { WixIcon } from 'icons/WixIcon';
import { WooCommerceIcon } from 'icons/WooCommerceIcon';
import { ReplaySubject, Subject } from 'rxjs';
import { UserService } from 'services/user-service';
import { SMART_ASSISTANT_TOKEN_FACTORS } from 'shared/constants/smart-assistant';
import {
  ChatMessage,
  SmartAssistantCompleteResponse,
  SmartAssistantMessageEvent,
  SmartAssistantMessageEventType,
  SmartAssistantPartialResponse
} from 'shared/interfaces/ChatMessage';
import { AccessLevel, SmartAssistant, SmartAssistantModel, SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { Subscription } from 'shared/interfaces/Subscription';
import { getEvents } from 'shared/utils/http-streaming';

export const getModelItems = (enableSpecialModels?: boolean, voiceModelsOnly?: boolean): SelectItemProps[] => {
  if (voiceModelsOnly)
    return [
      {
        icon: <IconBrandOpenai />,
        label: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_REALTIME.TITLE'),
        info: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_REALTIME.INFO'),
        value: SmartAssistantModel.GPT_4_O_REALTIME
      },
      {
        icon: <IconBrandOpenai />,
        label: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_MINI_REALTIME.TITLE'),
        info: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_MINI_REALTIME.INFO'),
        value: SmartAssistantModel.GPT_4_O_MINI_REALTIME
      }
    ];

  return [
    {
      icon: <IconBrandOpenai />,
      label: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_MINI.TITLE'),
      value: SmartAssistantModel.GPT_4_O_MINI,
      info: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O_MINI.INFO'),
      labelComponent: (
        <TokenFactorBadge
          width={250}
          factor={SMART_ASSISTANT_TOKEN_FACTORS.GPT_4_O_MINI}
          infoTranslationKey="SMART_ASSISTANT.MODEL.GPT_4_O_MINI.FACTOR_INFO"
          position="top"
        />
      )
    },
    {
      icon: <IconBrandOpenai />,
      label: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O.TITLE'),
      value: SmartAssistantModel.GPT_4_O,
      disabled: enableSpecialModels === true ? false : true,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.GPT_4_O}
            infoTranslationKey="SMART_ASSISTANT.MODEL.GPT_4_O.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.GPT_4_O.INFO')
    },
    {
      icon: <AnthropicIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_5_HAIKU.TITLE'),
      value: SmartAssistantModel.CLAUDE_3_5_HAIKU,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.CLAUDE_3_5_HAIKU}
            infoTranslationKey="SMART_ASSISTANT.MODEL.CLAUDE_3_5_HAIKU.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_5_HAIKU.INFO')
    },
    {
      icon: <AnthropicIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_5_SONNET.TITLE'),
      value: SmartAssistantModel.CLAUDE_3_5_SONNET,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.CLAUDE_3_5_SONNET}
            infoTranslationKey="SMART_ASSISTANT.MODEL.CLAUDE_3_5_SONNET.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_5_SONNET.INFO')
    },
    {
      icon: <AnthropicIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_7_SONNET.TITLE'),
      value: SmartAssistantModel.CLAUDE_3_7_SONNET,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.CLAUDE_3_7_SONNET}
            infoTranslationKey="SMART_ASSISTANT.MODEL.CLAUDE_3_7_SONNET.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.CLAUDE_3_7_SONNET.INFO')
    },
    {
      icon: <GoogleIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_2_0.TITLE'),
      value: SmartAssistantModel.GOOGLE_GEMINI_FLASH_2_0,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.GOOGLE_GEMINI_FLASH_2_0}
            infoTranslationKey="SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_2_0.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_2_0.INFO')
    },
    {
      icon: <GoogleIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_1_5.TITLE'),
      value: SmartAssistantModel.GOOGLE_GEMINI_FLASH_1_5,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.GOOGLE_GEMINI_FLASH_1_5}
            infoTranslationKey="SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_1_5.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_FLASH_1_5.INFO')
    },
    {
      icon: <GoogleIcon />,
      label: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_PRO_1_5.TITLE'),
      value: SmartAssistantModel.GOOGLE_GEMINI_PRO_1_5,
      labelComponent:
        enableSpecialModels === true || UserService.isCustomOrganisationMember() ? (
          <TokenFactorBadge
            width={250}
            factor={SMART_ASSISTANT_TOKEN_FACTORS.GOOGLE_GEMINI_PRO_1_5}
            infoTranslationKey="SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_PRO_1_5.FACTOR_INFO"
            position="top"
          />
        ) : (
          <UpgradePlanButton showLogo variant="filled" size="xs" />
        ),
      info: i18n.t('SMART_ASSISTANT.MODEL.GOOGLE_GEMINI_PRO_1_5.INFO')
    }
  ];
};

export const getAccessibilityItems = (): SelectItemProps[] => [
  {
    icon: <IconLockOpen />,
    label: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PUBLIC.TITLE'),
    value: AccessLevel.PUBLIC,
    info: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PUBLIC.INFO')
  },
  {
    icon: <IconPassword />,
    label: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PRIVATE.TITLE'),
    value: AccessLevel.PRIVATE,
    info: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PRIVATE.INFO')
  },
  {
    icon: <IconLock />,
    label: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PROTECTED.TITLE'),
    value: AccessLevel.PROTECTED,
    info: i18n.t('SMART_ASSISTANT.SHARE.ACCESS.PROTECTED.INFO')
  }
];

export const getSmartAssistantsAsSelectItems = (smartAssistants: SmartAssistant[]) => {
  return smartAssistants.map((smartAssistant) => ({
    label: smartAssistant.name,
    value: smartAssistant._id,
    iconComponent: <Avatar src={smartAssistant.avatarUrl} size="xs" radius="xl" />
  }));
};

export const SMART_ASSISTANT_TYPE_DATA: Record<
  SmartAssistantType,
  { title: string; description: string; icon: (size?: number) => React.ReactNode }
> = {
  [SmartAssistantType.BASIC]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.BASIC.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.BASIC.DESCRIPTION'),
    icon: (size = 20) => <LogoIcon w={size} h={size} />
  },
  [SmartAssistantType.WEBSITE]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.WEBSITE.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.WEBSITE.DESCRIPTION'),
    icon: (size = 20) => <BrowserIcon size={size} />
  },
  [SmartAssistantType.PHONE]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.PHONE.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.PHONE.DESCRIPTION'),
    icon: (size = 20) => <IconPhone style={{ width: size, height: size }} />
  },
  [SmartAssistantType.SHOPIFY]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.SHOPIFY.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.SHOPIFY.DESCRIPTION'),
    icon: (size = 20) => <ShopifyIcon w={size} h={size} />
  },
  [SmartAssistantType.INSTAGRAM]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.INSTAGRAM.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.INSTAGRAM.DESCRIPTION'),
    icon: (size = 20) => <InstagramIcon w={size} h={size} />
  },
  [SmartAssistantType.FACEBOOK]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.FACEBOOK.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.FACEBOOK.DESCRIPTION'),
    icon: (size = 20) => <FacebookIcon w={size} h={size} />
  },
  [SmartAssistantType.DISCORD]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.DISCORD.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.DISCORD.DESCRIPTION'),
    icon: (size = 20) => <DiscordIcon w={size} h={size} />
  },
  [SmartAssistantType.WHATSAPP]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.WHATSAPP.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.WHATSAPP.DESCRIPTION'),
    icon: (size = 20) => <WhatsAppIcon w={size} h={size} />
  },
  [SmartAssistantType.BIGCOMMERCE]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.BIGCOMMERCE.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.BIGCOMMERCE.DESCRIPTION'),
    icon: (size = 20) => <BigCommerceIcon w={size} h={size} />
  },
  [SmartAssistantType.WIX]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.WIX.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.WIX.DESCRIPTION'),
    icon: (size = 20) => <WixIcon w={size} h={size} />
  },
  [SmartAssistantType.WOOCOMMERCE]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.WOOCOMMERCE.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.WOOCOMMERCE.DESCRIPTION'),
    icon: (size = 20) => <WooCommerceIcon w={size} h={size} />
  },
  [SmartAssistantType.SQUARESPACE]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.SQUARESPACE.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.SQUARESPACE.DESCRIPTION'),
    icon: (size = 20) => <SquarespaceIcon w={size} h={size} />
  },
  [SmartAssistantType.SMS]: {
    title: i18n.t('SMART_ASSISTANT.TYPE.SMS.TITLE'),
    description: i18n.t('SMART_ASSISTANT.TYPE.SMS.DESCRIPTION'),
    icon: (size = 20) => <IconDeviceMobileMessage size={size} />
  }
};

export const getAgentGroups = (subscription: Subscription) => [
  {
    title: i18n.t('SMART_ASSISTANT.TYPE.GROUPS.GENERAL'),
    items: [
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.BASIC],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.BASIC].icon(20),
        type: SmartAssistantType.BASIC,
        isNew: false,
        isPreview: false,
        isAccessible: true
      },
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.WEBSITE],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.WEBSITE].icon(20),
        type: SmartAssistantType.WEBSITE,
        isNew: false,
        isPreview: false,
        isAccessible: subscription.product.smartAssistantIntegrations
      },
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.PHONE],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.PHONE].icon(20),
        type: SmartAssistantType.PHONE,
        isNew: false,
        isPreview: false,
        isAccessible: true
      }
    ]
  },
  {
    title: i18n.t('SMART_ASSISTANT.TYPE.GROUPS.MESSAGING'),
    items: [
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.DISCORD],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.DISCORD].icon(20),
        type: SmartAssistantType.DISCORD,
        isNew: false,
        isPreview: false,
        isAccessible: subscription.product.smartAssistantIntegrations
      },
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.WHATSAPP],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.WHATSAPP].icon(20),
        type: SmartAssistantType.WHATSAPP,
        isNew: false,
        isPreview: false,
        isAccessible: subscription.product.enableWhatsApp
      },
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.FACEBOOK],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.FACEBOOK].icon(20),
        type: SmartAssistantType.FACEBOOK,
        isNew: false,
        isPreview: false,
        isAccessible: subscription.product.enableFacebook
      },
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.INSTAGRAM],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.INSTAGRAM].icon(20),
        type: SmartAssistantType.INSTAGRAM,
        isNew: false,
        isPreview: false,
        isAccessible: subscription.product.enableInstagram
      }
    ]
  },
  {
    title: i18n.t('SMART_ASSISTANT.TYPE.GROUPS.SALES'),
    items: [
      {
        ...SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.SHOPIFY],
        icon: SMART_ASSISTANT_TYPE_DATA[SmartAssistantType.SHOPIFY].icon(20),
        type: SmartAssistantType.SHOPIFY,
        isNew: false,
        isPreview: false,
        isAccessible: true
      }
    ]
  }
];

export const handleSmartAssistantAnswer = (
  progressEvent: AxiosProgressEvent,
  accumulatedMessageEvents: SmartAssistantMessageEvent[],
  eventSubject?: Subject<SmartAssistantMessageEvent>,
  partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>
): SmartAssistantMessageEvent[] => {
  const allEvents = getEvents(progressEvent.event?.currentTarget?.response);
  const newEvents = allEvents.slice(accumulatedMessageEvents.length);

  for (const event of newEvents) {
    let smartAssistantEvent: SmartAssistantMessageEvent;

    // If we fail to parse the current event, we return the accumulated message events so far
    try {
      smartAssistantEvent = JSON.parse(event);
    } catch {
      return accumulatedMessageEvents;
    }

    accumulatedMessageEvents.push(smartAssistantEvent);

    if (smartAssistantEvent.type === SmartAssistantMessageEventType.STREAM_END) {
      eventSubject?.next(smartAssistantEvent);
      // We want to push the stream end event to the partial response subject so that it can know when to complete
      partialResponseSubject?.next(smartAssistantEvent);

      return accumulatedMessageEvents;
    }

    if (smartAssistantEvent.type === SmartAssistantMessageEventType.PARTIAL_RESPONSE) {
      partialResponseSubject?.next(smartAssistantEvent);
    }

    if (smartAssistantEvent.type === SmartAssistantMessageEventType.ERROR) {
      const errorMessage = 'Smart assistant ask error';
      // Send the error to the partial response subject so that it will automatically complete
      partialResponseSubject?.error(errorMessage);

      throw new Error(errorMessage);
    }

    if (smartAssistantEvent.type === SmartAssistantMessageEventType.TOOL_EVENT) {
      eventSubject?.next(smartAssistantEvent);
    }
  }

  return accumulatedMessageEvents;
};

export const reducePartialResponses = (events: SmartAssistantMessageEvent[]) => {
  let partialResponseText = '';

  return events.reduce((acc, event) => {
    if (event.type === SmartAssistantMessageEventType.PARTIAL_RESPONSE) {
      partialResponseText += (event.data as SmartAssistantPartialResponse).text;
    } else {
      if (partialResponseText) {
        acc.push({
          type: SmartAssistantMessageEventType.COMPLETE_RESPONSE,
          data: { text: partialResponseText } as SmartAssistantCompleteResponse
        });
        partialResponseText = '';
      }
      acc.push(event);
    }
    return acc;
  }, [] as SmartAssistantMessageEvent[]);
};

export const transformChatMessageWithPartialResponse = (chatMessage: ChatMessage) => {
  if (!chatMessage.partialResponseSubject) return chatMessage;

  let text = '';
  chatMessage.partialResponseSubject
    .subscribe((bufferContent) => {
      if (bufferContent.type === SmartAssistantMessageEventType.PARTIAL_RESPONSE) {
        text += (bufferContent.data as SmartAssistantPartialResponse).text;
      }
    })
    .unsubscribe();

  return {
    ...chatMessage,
    text,
    partialResponseSubject: undefined
  };
};

export const canPauseSmartAssistant = (smartAssistant: SmartAssistant) =>
  smartAssistant.type === SmartAssistantType.FACEBOOK || smartAssistant.type === SmartAssistantType.INSTAGRAM; // Add more types as they are supported

export const buildLinearGradientValues = (colors: string[]): { percentage: number; color: string }[] => {
  return colors.map((color, index) => {
    const percentage = index === 0 ? 0 : index === colors.length - 1 ? 100 : (index / (colors.length - 1)) * 100;
    return { percentage, color };
  });
};

export const buildSmartAssistantLinearGradientStyle = (colors: string[]) => {
  const gradientElements = buildLinearGradientValues(colors);
  const gradientString = gradientElements.map(({ percentage, color }) => `${color} ${percentage}%`).join(', ');
  return {
    background: `linear-gradient(90deg, ${gradientString})`,
    opacity: 0.4,
    filter: 'blur(85px)'
  };
};
